import { useEffect, useState } from "react";

/**
 * `usePageVisibility`:
 * Does things when the page is backgrounded (or another tab is open).
 * See: https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
 *
 * There are two common patterns of use for this component, and one highly recommended pattern.
 *
 * `usePageVisibiltyPolling:`
 * This is just one pattern, but demonstrates the power of `usePageVisibility`.
 * It will only trigger a `refetch` if you have been backgrounded for longer than the `POLL_INTERVAL`.
 * ```
 * const POLL_INTERVAL = 60000;
 * const { refetch, startPolling, stopPolling } = useQuery(QUERY_NAME);
 * usePageVisibility(({ isVisible, changedFor }) => {
 *   if (isVisible) {
 *     if (changedFor > POLL_INTERVAL) {
 *       refetch()
 *     }
 *     startPolling(POLL_INTERVAL);
 *   } else {
 *     stopPolling();
 *   }
 * });
 * ```
 *
 * `Common Patterns:`
 * I recommend these examples less, but they do *work*.
 * ```
 * // In this example, queries will *immediately* poll when a backgrounded tab is no longer backgrounded.
 * // Note that this causes additional renders, and greedily refetches. Not ideal.
 * const { isVisible } = usePageVisibilty();
 * useQuery(QUERY_NAME, { skip: isVisible });
 * ```
 * or:
 * ```
 * // In this example, queries will wait for the pollInterval to pass before polling when a backgrounded tab is no longer backgrounded.
 * // Note that this causes less renders / refetches, but you have to wait for the pollInterval for a refetch.
 * const { startPolling, stopPolling } = useQuery(QUERY_NAME);
 * usePageVisibilty(({ isVisible }) => isVisible && ? startPolling(POLL_INTERVAL) : stopPolling());
 * ```
 */
export interface PageVisibilityProps {
  isVisible: boolean;
  changedFor: number;
  changedAt?: number;
}

export const usePageVisibility = (
  onChange?: ({ isVisible, changedFor }: PageVisibilityProps) => void,
) => {
  const [state, setState] = useState({
    isVisible: !document.hidden,
    changedAt: new Date().getTime(),
    changedFor: 0,
  });

  const onVisibilityChange = () => {
    const newChangedAt = new Date().getTime();
    const newState = {
      isVisible: !document.hidden,
      changedAt: newChangedAt,
      changedFor: newChangedAt - state.changedAt,
    };

    setState(newState);
    if (onChange) {
      onChange(newState);
    }
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", onVisibilityChange, false);
    return () => {
      document.removeEventListener(
        "visibilitychange",
        onVisibilityChange,
        false,
      );
    };
  }, [state.isVisible]);

  return state;
};
